import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from '../../common/context/AuthContext'
import { AlertCircle, Printer, Utensils, Plus, X, Phone, Loader2, Edit3, Trash2, ChefHat } from "lucide-react"
import { API_URL } from '../../api/config'

// FIXED: Helper function to get item name consistently
const getItemName = (item) => {
  return item.itemName || item.item_name || item.name || 'Unknown Item';
}

const Modal = ({ isOpen, onClose, title, message, actions = [] }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="py-2">
          <p className="text-gray-600">{message}</p>
        </div>

        <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-100">
          {actions.length === 0 ? (
            <button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded transition">
              OK
            </button>
          ) : (
            actions.map((action, index) => (
              <button
                key={index}
                onClick={() => {
                  if (action.onPress) action.onPress()
                  if (!action.keepOpen) onClose()
                }}
                className={
                  action.isDestructive
                    ? "bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded transition"
                    : "bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded transition"
                }
              >
                {action.text}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

const HoldBillDetails = ({ bill, onClose, onPrintKOT, onEdit, onSaveAndPrint, onDelete }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800">Hold Bill Details</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {(bill.names || bill.mobileNumber) && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              {bill.names && (
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Names:</span> {bill.names}
                </p>
              )}
              {bill.mobileNumber && (
                <p className="text-sm text-gray-700 flex items-center gap-1 mt-1">
                  <Phone size={14} />
                  <span className="font-semibold">Mobile:</span> {bill.mobileNumber}
                </p>
              )}
            </div>
          )}

          <div className="mb-4">
            <h4 className="font-semibold text-gray-800 mb-3">Items ({bill.items?.length || 0})</h4>
            <div className="space-y-2">
              {bill.items?.map((item, index) => {
                const itemName = getItemName(item);
                return (
                  <div key={index} className="flex justify-between items-start p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{itemName}</p>
                      {item.variant && (
                        <p className="text-sm text-gray-600">Variant: {item.variant.name}</p>
                      )}
                      {item.note && (
                        <p className="text-xs text-gray-500 mt-1 italic">Note: {item.note}</p>
                      )}
                      <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-semibold text-gray-800">
                      ₹{((item.variant?.price || item.price) * item.quantity).toFixed(2)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">₹{bill.subtotal?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">CGST (2.5%):</span>
                <span className="font-medium">₹{bill.cgst?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">SGST (2.5%):</span>
                <span className="font-medium">₹{bill.sgst?.toFixed(2)}</span>
              </div>
              {(bill.discountPercentage > 0 || bill.discountAmount > 0) && (
                <div className="flex justify-between text-green-600">
                  <span>Discount:</span>
                  <span className="font-medium">
                    -₹{bill.discountAmount?.toFixed(2)}
                    {bill.discountPercentage > 0 && ` (${bill.discountPercentage}%)`}
                  </span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-gray-200 text-lg font-bold text-blue-600">
                <span>Total:</span>
                <span>₹{bill.totalAmount?.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-6">
            <button
              onClick={onEdit}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors font-semibold"
            >
              <Edit3 size={18} />
              <span>Edit Bill</span>
            </button>
            <button
              onClick={onPrintKOT}
              className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-4 rounded-lg transition-colors font-semibold"
            >
              <ChefHat size={18} />
              <span>Print KOT</span>
            </button>
            <button
              onClick={onSaveAndPrint}
              className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg transition-colors font-semibold"
            >
              <Printer size={18} />
              <span>Save & Print</span>
            </button>
            <button
              onClick={onDelete}
              className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg transition-colors font-semibold"
            >
              <Trash2 size={18} />
              <span>Delete Bill</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const TableCard = ({ table, isSelected, onTableClick, hasHoldBills, tableBills, onQuickActions }) => {
  let borderColor = 'border-slate-100'
  let dotColor = 'bg-slate-300'
  let bgColor = 'bg-white'

  if (hasHoldBills) {
    dotColor = 'bg-rose-500'
    borderColor = 'border-rose-200'
    bgColor = 'bg-rose-50/30'
  }

  return (
    <div className={`relative rounded-2xl border ${borderColor} ${bgColor} transition-all overflow-hidden`}>

      {/* Table header — always clickable */}
      <div
        onClick={() => onTableClick(table.id)}
        className="p-4 md:p-5 cursor-pointer hover:bg-white/60 transition-all flex flex-col justify-between min-h-[7rem]"
      >
        <div className="flex justify-between items-start">
          <div>
            <h4 className="text-lg md:text-xl font-extrabold text-slate-800">{table.tableName}</h4>
            {table.tableNumber && (
              <p className="text-xs text-slate-400 font-semibold mt-0.5">#{table.tableNumber}</p>
            )}
          </div>
          <div className={`w-2.5 h-2.5 rounded-full mt-1 ${dotColor}`} />
        </div>

        <div className="mt-2">
          {hasHoldBills ? (
            <div className="flex items-center justify-between">
              <span className="text-[10px] md:text-[11px] font-bold text-rose-500">
                {tableBills.length} Hold Bill{tableBills.length > 1 ? 's' : ''}
              </span>
              <span className="text-[10px] font-bold text-slate-600">
                ₹{tableBills.reduce((sum, b) => sum + (b.totalAmount || 0), 0).toFixed(2)}
              </span>
            </div>
          ) : (
            <p className="text-[10px] md:text-[11px] font-bold text-slate-300">Available — tap to open</p>
          )}
        </div>
      </div>

      {/* Hold bill items — always visible, no toggle */}
      {hasHoldBills && (
        <div className="border-t border-rose-100 px-4 pt-3 pb-4 space-y-3">
          {tableBills.map((bill, billIndex) => (
            <div key={bill.id} className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">

              {/* Bill header */}
              <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-100">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">
                  Bill #{billIndex + 1}{bill.names ? ` · ${bill.names}` : ''}
                </span>
                <span className="text-[11px] font-extrabold text-blue-600">₹{bill.totalAmount?.toFixed(2)}</span>
              </div>

              {/* Items list */}
              <div className="px-3 py-2 space-y-1">
                {bill.items?.map((item, idx) => {
                  const itemName = getItemName(item)
                  return (
                    <div key={idx} className="flex justify-between items-center">
                      <span className="text-[10px] text-slate-700 truncate flex-1 font-medium">
                        {itemName}{item.variant ? ` (${item.variant.name})` : ''}
                      </span>
                      <span className="text-[10px] text-slate-400 font-semibold ml-2 shrink-0">×{item.quantity}</span>
                    </div>
                  )
                })}
              </div>

              {/* Edit + Delete only */}
              <div className="grid grid-cols-2 gap-2 px-3 pb-3 pt-1">
                <button
                  onClick={(e) => { e.stopPropagation(); onQuickActions.onEdit(bill) }}
                  className="flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-[11px] font-bold transition-colors"
                >
                  <Edit3 size={12} />
                  Edit
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onQuickActions.onDelete(bill) }}
                  className="flex items-center justify-center gap-1.5 bg-rose-500 hover:bg-rose-600 text-white py-2 rounded-lg text-[11px] font-bold transition-colors"
                >
                  <Trash2 size={12} />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const NewBill = () => {
  const [tables, setTables] = useState([])
  const [holdBills, setHoldBills] = useState({})
  const [loading, setLoading] = useState(true)
  const [processingBill, setProcessingBill] = useState(false)
  const [selectedHoldBill, setSelectedHoldBill] = useState(null)
  const { userToken, restaurantId } = useAuth()
  const navigate = useNavigate()

  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    actions: [],
  })

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await Promise.all([fetchTables(), fetchHoldBills()])
      setLoading(false)
    }

    loadData()
  }, [])

  const fetchTables = async () => {
    try {
      const response = await fetch(`${API_URL}/tables`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      })
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || "Failed to fetch tables")
      }
      const result = await response.json()
      const tablesData = Array.isArray(result) ? result : (result.data || [])
      setTables(tablesData)
    } catch (err) {
      console.error("Error fetching tables:", err)
      showModal("Error", `Failed to load tables: ${err.message}. Please try again.`)
    }
  }

  const fetchHoldBills = async () => {
    try {
      const response = await fetch(`${API_URL}/hold-bills`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      })
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || "Failed to fetch hold bills")
      }
      const result = await response.json()
      const billsData = Array.isArray(result) ? result : (result.data || [])
      
      const holdBillsByTable = billsData.reduce((acc, bill) => {
        if (bill.status === "HOLD") {
          if (!acc[bill.tableId]) {
            acc[bill.tableId] = []
          }
          acc[bill.tableId].push(bill)
        }
        return acc
      }, {})
      setHoldBills(holdBillsByTable)
    } catch (err) {
      console.error("Error fetching hold bills:", err)
      showModal("Error", `Failed to load hold bills: ${err.message}. Please try again.`)
    }
  }

  const showModal = (title, message, actions = []) => {
    setModal({
      isOpen: true,
      title,
      message,
      actions,
    })
  }

  const closeModal = () => {
    setModal({
      ...modal,
      isOpen: false,
    })
  }

  const handleTableClick = async (tableId) => {
    const hasHoldBills = holdBills[tableId]?.length > 0

    if (hasHoldBills) {
      // If exactly one hold bill, go straight into edit mode with existing items loaded
      const bills = holdBills[tableId]
      if (bills.length === 1) {
        handleEditBill(bills[0])
      } else {
        // Multiple hold bills — show the first one in the details modal
        setSelectedHoldBill(bills[0])
      }
      return
    }

    // Free table — navigate directly, no proceed step
    try {
      setProcessingBill(true)
      await updateTableStatus(tableId)
      navigate('/menu-selection', { state: { tableId } })
    } catch (err) {
      showModal('Error', err.message || 'Failed to select table. Please try again.')
    } finally {
      setProcessingBill(false)
    }
  }

  const handleViewHoldBill = (bill) => {
    setSelectedHoldBill(bill)
  }

  const handleEditBill = (bill) => {
    setSelectedHoldBill(null)
    
    // FIXED: Transform items with proper name handling
    const transformedItems = bill.items.map((item) => ({
      id: item.productId || item.id || item._id,
      _id: item.productId || item.id || item._id,
      item_name: getItemName(item),
      itemName: getItemName(item),
      quantity: item.quantity || 1,
      price: item.price,
      pricingType: item.pricingType,
      category: item.category,
      selectedVariant: item.variant,
      variant: item.variant,
      names: item.names || "",
      note: item.note || "",
    }))
    
    navigate("/menu-selection", {
      state: {
        tableId: bill.tableId,
        existingHoldBillId: bill.id || bill._id,
        existingItems: transformedItems,
        isAddingItems: true,
      },
    })
  }

  const handlePrintKOT = async (bill, table) => {
    setProcessingBill(true)
    try {
      const kotResponse = await fetch(`${API_URL}/kot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          tableId: bill.tableId,
          holdBillId: bill.id || bill._id,
          items: bill.items,
          customerName: bill.names || '',
          timestamp: new Date().toISOString(),
        }),
      })

      if (!kotResponse.ok) {
        const errorData = await kotResponse.json().catch(() => ({}))
        throw new Error(errorData.message || 'Failed to send KOT')
      }

      setSelectedHoldBill(null)
      navigate(`/print-kot/${bill.tableId}`, {
        state: {
          currentBill: {
            _id: bill.id || bill._id,
            items: bill.items,
            names: bill.names,
            mobileNumber: bill.mobileNumber,
            discountPercentage: bill.discountPercentage,
            billAmount: bill.totalAmount
          },
          tableNumber: table?.tableNumber || table?.tableName
        }
      })
    } catch (error) {
      console.error('Error printing KOT:', error)
      showModal("Error", `Failed to print KOT: ${error.message}`)
    } finally {
      setProcessingBill(false)
    }
  }

const handleSaveAndPrint = async (bill) => {
  setProcessingBill(true)
  try {
    // CRITICAL: The bill object passed here should already contain the LATEST items
    // This function is called from HoldBillDetails modal after viewing a hold bill
    
    console.log('=== SAVE AND PRINT FROM NEWBILL ===');
    console.log('Bill items being finalized:', bill.items);
    
    const formattedItems = bill.items.map((item) => ({
      productId: item.productId || item.id || item._id,
      item_name: getItemName(item),
      quantity: item.quantity || 1,
      category: item.category || 'Veg',
      pricingType: item.pricingType || 'basePrice',
      price: item.variant ? item.variant.price : item.price,
      variant: item.variant,
      names: item.names || '',
      note: item.note || '',
    }))

    console.log('Formatted items for save:', formattedItems);

    // Save the bill - backend will fetch from hold_bills table
    const saveResponse = await fetch(`${API_URL}/bills/${bill.tableId}/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      },
      body: JSON.stringify({
        discountPercentage: bill.discountPercentage || 0,
        discountAmount: bill.discountAmount || 0,
        items: formattedItems,
        names: bill.names || '',
        mobileNumber: bill.mobileNumber || '',
        paymentMode: bill.paymentMode || 'Cash'
      }),
    })

    if (!saveResponse.ok) {
      const errorData = await saveResponse.json().catch(() => ({}))
      throw new Error(errorData.message || 'Failed to save bill')
    }

    const saveData = await saveResponse.json()
    console.log('Bill saved successfully:', saveData);

    // Delete the hold bill
    await fetch(`${API_URL}/hold-bills/${bill.id || bill._id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    })

    await fetchHoldBills()
    setSelectedHoldBill(null)

    const savedBill = saveData.bill || saveData
    const billId = savedBill.id || savedBill._id

    console.log('Navigating to print with bill:', billId);
    console.log('=== END SAVE AND PRINT ===');

    navigate(`/print-bill/${billId}`, {
      state: {
        billId: billId,
        billData: {
          id: billId,
          _id: billId,
          billNumber: savedBill.billNumber,
          items: formattedItems,
          totalAmount: bill.totalAmount,
          names: bill.names || '',
          mobileNumber: bill.mobileNumber || '',
          discountPercentage: bill.discountPercentage || 0,
          discountAmount: bill.discountAmount || 0,
          paymentMode: bill.paymentMode || 'Cash',
          createdAt: savedBill.createdAt || new Date().toISOString(),
          tableId: bill.tableId
        },
      },
    })
  } catch (error) {
    console.error('Error saving and printing bill:', error)
    showModal("Error", `Failed to save and print bill: ${error.message}`)
  } finally {
    setProcessingBill(false)
  }
}

  const handleDeleteBill = (bill) => {
    showModal(
      "Delete Hold Bill",
      "Are you sure you want to delete this hold bill? This action cannot be undone.",
      [
        {
          text: "Cancel",
          isDestructive: false,
        },
        {
          text: "Delete",
          isDestructive: true,
          onPress: async () => {
            setProcessingBill(true)
            try {
              const response = await fetch(`${API_URL}/hold-bills/${bill.id || bill._id}`, {
                method: 'DELETE',
                headers: {
                  Authorization: `Bearer ${userToken}`,
                },
              })

              if (!response.ok) {
                throw new Error('Failed to delete hold bill')
              }

              await fetchHoldBills()
              setSelectedHoldBill(null)
              
              showModal("Success", "Hold bill deleted successfully")
            } catch (error) {
              console.error('Error deleting hold bill:', error)
              showModal("Error", `Failed to delete hold bill: ${error.message}`)
            } finally {
              setProcessingBill(false)
            }
          },
        },
      ]
    )
  }

  const updateTableStatus = async (tableId) => {
    const tableResponse = await fetch(`${API_URL}/tables/${tableId}/status`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      body: JSON.stringify({ status: "Occupied" }),
    })
    if (!tableResponse.ok) throw new Error("Failed to update table status")
  }

  return (
    <div className="max-w-7xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500 pb-32 md:pb-36 px-4 md:px-0">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 mt-4 md:mt-0">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight mb-2">Select Table</h1>
          <div className="flex items-center gap-3 md:gap-4">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] md:text-xs font-bold">1</span>
              <span className="text-xs md:text-sm font-bold text-blue-600">Choose Table</span>
            </div>
            <div className="h-px w-4 md:w-8 bg-slate-200"></div>
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-slate-200 text-slate-400 flex items-center justify-center text-[10px] md:text-xs font-bold">2</span>
              <span className="text-xs md:text-sm font-bold text-slate-400">Add Items</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Zones Section */}
      <div className="bg-white p-4 md:p-6 rounded-2xl border border-slate-200 shadow-sm mb-8 overflow-hidden">
        <div className="flex flex-col gap-6">
          <div className="flex flex-wrap items-center gap-4 md:gap-6 text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full border border-slate-200"></div>
              <span>Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-rose-500"></div>
              <span>Occupied / Has Hold Bills</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500 ring-2 ring-blue-100"></div>
              <span className="text-blue-600">Selected</span>
            </div>
          </div>
        </div>
      </div>

      {/* Table Sections */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
        </div>
      ) : (
        <div className="space-y-10">
          {tables.length > 0 ? (
            <div className="animate-in fade-in slide-in-from-left-4 duration-500">
              <div className="flex items-baseline gap-3 mb-4 md:mb-6">
                <h2 className="text-[10px] md:text-xs font-black text-slate-400 uppercase tracking-[0.2em]">All Tables</h2>
                <span className="text-[9px] md:text-[10px] font-bold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full uppercase tracking-tighter">{tables.length} Tables</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
                {tables.map((table) => {
                  const hasHoldBills = holdBills[table.id]?.length > 0
                  const tableBills = holdBills[table.id] || []
                  
                  return (
                    <TableCard 
                      key={table.id} 
                      table={table} 
                      isSelected={false}
                      onTableClick={handleTableClick}
                      hasHoldBills={hasHoldBills}
                      tableBills={tableBills}
                      onQuickActions={{
                        onEdit: handleEditBill,
                        onPrintKOT: (bill) => handlePrintKOT(bill, table),
                        onDelete: handleDeleteBill
                      }}
                    />
                  )
                })}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-md p-12 text-center border border-slate-200">
              <AlertCircle className="h-16 w-16 mx-auto text-amber-500 mb-4" />
              <h2 className="text-2xl font-black text-slate-900 mb-2">No Tables Found</h2>
              <p className="text-slate-600 mb-6 font-medium">There are no tables set up yet. Create some tables to get started.</p>
              <button
                className="bg-slate-900 hover:bg-slate-800 text-white py-3 px-6 rounded-xl transition-all font-bold inline-flex items-center gap-2"
                onClick={() => navigate("/table-manager")}
              >
                <Utensils size={18} />
                Manage Tables
              </button>
            </div>
          )}
        </div>
      )}

      {processingBill && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-2xl shadow-xl flex flex-col items-center">
            <Loader2 className="animate-spin text-blue-600 mb-4" size={48} />
            <p className="text-lg font-bold text-slate-900">Processing...</p>
          </div>
        </div>
      )}

      {selectedHoldBill && (
        <HoldBillDetails
          bill={selectedHoldBill}
          onClose={() => setSelectedHoldBill(null)}
          onEdit={() => handleEditBill(selectedHoldBill)}
          onPrintKOT={() => {
            const table = tables.find(t => t.id === selectedHoldBill.tableId)
            handlePrintKOT(selectedHoldBill, table)
          }}
          onSaveAndPrint={() => handleSaveAndPrint(selectedHoldBill)}
          onDelete={() => handleDeleteBill(selectedHoldBill)}
        />
      )}

      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        actions={modal.actions}
      />
    </div>
  )
}

export default NewBill
